import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Flex, HStack, VStack } from "../../../../styled-system/jsx";
import { css } from "../../../../styled-system/css";
import { Button } from "../../../components/core/basic/Button";
import { AlertSection } from "../../uploads/AlertSection";
import { flattenUploadAlerts } from "../../uploads/alertUtils";
import { errorToast } from "../../../utils/toasts";
import type { Upload } from "../../../zodios/apiDefinitions/uploads";
import { usePricingPlanRows } from "./api/usePricingPlanRows";
import { useCommitPricingPlanUpload } from "./api/useCommitPricingPlanUpload";
import { downloadPricingPlanUploadCsv } from "./api/downloadPricingPlanUploadCsv";

interface ReviewStepProps {
  upload: Upload;
  onCommitStarted: () => void;
}

export function ReviewStep({ upload, onCommitStarted }: ReviewStepProps) {
  const { t } = useTranslation(["pricingv2"]);
  const totals = {
    valid: upload.valid_rows ?? 0,
    warning: upload.warning_rows ?? 0,
    invalid: upload.invalid_rows ?? 0,
  };
  const issuesQuery = usePricingPlanRows(upload.id, { status: "warning,invalid", limit: 100 });
  const commitMutation = useCommitPricingPlanUpload(upload.id);
  const [isCommitting, setIsCommitting] = useState(false);

  const { errors: uploadLevelErrors, warnings: uploadLevelWarnings } = flattenUploadAlerts(upload);

  const errorItems = [
    ...uploadLevelErrors,
    ...(issuesQuery.data?.results.flatMap((row) =>
      row.errors.map((e) => ({ row: row.row_number, field: e.field, message: e.message })),
    ) ?? []),
  ];
  const warningItems = [
    ...uploadLevelWarnings,
    ...(issuesQuery.data?.results.flatMap((row) =>
      row.warnings.map((w) => ({ row: row.row_number, field: w.field, message: w.message })),
    ) ?? []),
  ];

  const handleCommit = async (ignoreWarnings: boolean) => {
    try {
      setIsCommitting(true);
      await commitMutation.mutateAsync({ ignoreWarnings });
      onCommitStarted();
    } catch (e) {
      errorToast({ title: t(($) => $.pricingv2["Commit failed"]), error: e });
    } finally {
      setIsCommitting(false);
    }
  };

  return (
    <VStack gap="large" alignItems="stretch">
      <HStack gap="xlarge">
        <CountTile label={t(($) => $.pricingv2.Valid)} value={totals.valid} color="green.600" />
        <CountTile label={t(($) => $.pricingv2.Warnings)} value={totals.warning} color="yellow.600" />
        <CountTile label={t(($) => $.pricingv2.Errors)} value={totals.invalid} color="red.600" />
      </HStack>

      <Flex gap="medium">
        <Button variant="secondary" onPress={() => void downloadPricingPlanUploadCsv(upload.id, "errors")}>
          {t(($) => $.pricingv2["Download error CSV"])}
        </Button>
      </Flex>

      <div className={css({ maxHeight: "400px", overflowY: "auto" })}>
        <VStack gap="large">
          {errorItems.length > 0 && <AlertSection type="error" items={errorItems} />}
          {warningItems.length > 0 && <AlertSection type="warning" items={warningItems} />}
        </VStack>
      </div>

      <HStack gap="medium" justifyContent="end">
        <Button
          isDisabled={totals.valid === 0 || isCommitting}
          isPending={isCommitting && totals.warning === 0}
          onPress={() => void handleCommit(false)}
        >
          {t(($) => $.pricingv2["Commit {count} valid rows"], { count: totals.valid })}
        </Button>
        {totals.warning > 0 && (
          <Button
            variant="secondary"
            isDisabled={isCommitting}
            isPending={isCommitting}
            onPress={() => void handleCommit(true)}
          >
            {t(($) => $.pricingv2["Commit {count} (including warnings)"], { count: totals.valid + totals.warning })}
          </Button>
        )}
      </HStack>
    </VStack>
  );
}

function CountTile({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <VStack alignItems="start" gap="xsmall">
      <span className={css({ fontSize: "small", color: "gray.600" })}>{label}</span>
      <span className={css({ fontSize: "xxxlarge", fontWeight: "bold", color })}>{value}</span>
    </VStack>
  );
}
